import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { MapPin, Menu, Sun, Moon, User, Coins, X, Plus } from "lucide-react";
import { useState, useEffect } from "react";
import { useTheme } from "@/lib/ThemeProvider";
import { useAppSelector, useAppDispatch } from "@/lib/hooks";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const { isAuthenticated, profile } = useAppSelector(state => state.user);
  const { balance } = useAppSelector(state => state.tokens);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <span className="text-xl font-bold text-foreground">Ulixes</span>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/guides" className="text-muted-foreground hover:text-primary transition-colors">
                Public Guides
              </Link>
              <Link to="/pricing" className="text-muted-foreground hover:text-primary transition-colors">
                Pricing
              </Link>
              <Link to="/about" className="text-muted-foreground hover:text-primary transition-colors">
                About Us
              </Link>
              <Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors">
                Contact
              </Link>
            </nav>

            <div className="hidden md:flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="w-10 h-10 rounded-full"
              >
                {isDark ? <Sun className="h-5 w-5"/> : <Moon className="h-5 w-5"/>}
              </Button>

              {isAuthenticated ? (
                <>
                  <Link to="/create-guide"
                      className="inline-flex items-center
                                  px-4 py-2 rounded-md
                                  bg-primary text-white
                                  hover:bg-primary/90
                                  focus-visible:outline-none
                                  focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
                                  transition-colors">
                    <Plus className="h-5 w-5 mr-2"/>
                    Create Guide
                  </Link>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full">
                    <Coins className="h-4 w-4 text-primary"/>
                    <span className="text-sm font-medium">{balance}</span>
                  </div>
                  <Link to="/profile">
                    <Button variant="ghost">
                      <User className="h-5 w-5 mr-2"/>
                      {profile.name || 'Account'}
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="hover:bg-primary/10">Login</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-primary hover:bg-primary/90 text-white">Sign Up</Button>
                  </Link>
                </>
              )}
            </div>

            <div className="md:hidden flex items-center space-x-2">
              {isAuthenticated && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-full">
                  <Coins className="h-4 w-4 text-primary"/>
                  <span className="text-sm font-medium">{balance}</span>
                </div>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="w-10 h-10 rounded-full"
              >
                {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileMenuOpen(true)}
                className="w-10 h-10"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          <div className="fixed inset-y-0 right-0 w-[300px] bg-background shadow-2xl">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <Link
                  to="/"
                  className="flex items-center space-x-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <MapPin className="h-6 w-6 text-primary" />
                  <span className="text-xl font-bold">Ulixes App</span>
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="h-8 w-8"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <nav className="flex-1 overflow-y-auto">
                <div className="space-y-2 p-6">
                  <Link
                    to="/guides"
                    className="flex items-center h-12 px-4 rounded-lg hover:bg-primary/10 text-foreground transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Public Guides
                  </Link>

                  {isAuthenticated && (
                    <Link
                      to="/create-guide"
                      className="flex items-center h-12 px-4 rounded-lg hover:bg-primary/10 text-foreground transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Create Guide
                    </Link>
                  )}

                  <Link
                    to="/pricing"
                    className="flex items-center h-12 px-4 rounded-lg hover:bg-primary/10 text-foreground transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Pricing
                  </Link>
                  <Link
                    to="/about"
                    className="flex items-center h-12 px-4 rounded-lg hover:bg-primary/10 text-foreground transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    About Us
                  </Link>
                  <Link
                    to="/contact"
                    className="flex items-center h-12 px-4 rounded-lg hover:bg-primary/10 text-foreground transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Contact
                  </Link>
                </div>
              </nav>

              <div className="p-6 border-t border-border">
                <div className="space-y-4">
                  {isAuthenticated ? (
                    <>
                      <Link to="/profile" onClick={() => setIsMobileMenuOpen(false)}>
                        <Button variant="outline" className="w-full justify-start h-12">
                          <User className="h-5 w-5 mr-3" />
                          {profile.name || 'Account'}
                        </Button>
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                        <Button variant="outline" className="w-full justify-center h-12 border-primary/20 hover:bg-primary/10 hover:border-primary/40">
                          Login
                        </Button>
                      </Link>
                      <Link to="/register" onClick={() => setIsMobileMenuOpen(false)}>
                        <Button className="w-full justify-center h-12 bg-primary hover:bg-primary/90 text-white font-medium mt-5">
                          Sign Up
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
